// ============================================================================
// CommunityGallery.jsx — Zenna Community Gallery & Inspiration Feed
// Masonry grid, likes, comments, filtering, share integration
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import ShareButtons from './ShareButtons'
import ZennaPageHeader from './ZennaPageHeader'

const API_URL = import.meta.env.VITE_API_URL || ''

// ============================================================================
// GALLERY POST CARD
// ============================================================================

function PostCard({ post, userEmail, onLike, onComment, onViewPost, onRemix, onToggleFavorite }) {
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showShare, setShowShare] = useState(false)

  const loadComments = async () => {
    try {
      const res = await fetch(`${API_URL}/community/gallery/${post.id}/comments`)
      const data = await res.json()
      setComments(data.comments || [])
    } catch (e) { console.error(e) }
  }

  const handleToggleComments = () => {
    if (!showComments && comments.length === 0) loadComments()
    setShowComments(!showComments)
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim() || submitting) return
    setSubmitting(true)
    try {
      const res = await fetch(`${API_URL}/community/gallery/${post.id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({ content: newComment.trim() })
      })
      const data = await res.json()
      if (data.success) {
        setComments(prev => [...prev, data.comment])
        setNewComment('')
        if (onComment) onComment(post.id)
      }
    } catch (e) { console.error(e) }
    setSubmitting(false)
  }

  return (
    <div className="bg-slate-800/80 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-slate-600/50 transition-all group">
      {/* Image */}
      <div className="relative cursor-pointer" onClick={() => onViewPost && onViewPost(post)}>
        <img
          src={post.thumbnail_url || post.image_urls?.[0]}
          alt={post.title}
          className="w-full object-cover"
          loading="lazy"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex items-center gap-4 text-white">
            <span className="flex items-center gap-1 text-sm font-medium">
              ❤️ {post.like_count || 0}
            </span>
            <span className="flex items-center gap-1 text-sm font-medium">
              💬 {post.comment_count || 0}
            </span>
          </div>
        </div>

        {/* Challenge badge */}
        {post.challenge_id && (
          <div className="absolute top-2 left-2 bg-amber-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            🏆 Challenge Entry
          </div>
        )}

        {/* Marketplace link */}
        {post.listing_id && (
          <div className="absolute top-2 right-2 bg-zenna-violet/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            🛒 For Sale
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Author row */}
        <div className="flex items-center gap-2 mb-2">
          {post.author?.avatar_url ? (
            <img src={post.author.avatar_url} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center text-[10px] text-white font-bold">
              {(post.author?.display_name || '?')[0].toUpperCase()}
            </div>
          )}
          <span className="text-xs text-slate-300 font-medium truncate flex-1">
            {post.author?.display_name || 'Anonymous'}
          </span>
          {post.author?.badge === 'founding' && (
            <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">⭐ Founder</span>
          )}
          {post.craft_type && (
            <span className="text-[9px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded-full">{post.craft_type}</span>
          )}
        </div>

        {/* Title */}
        {post.title && (
          <h3 className="text-sm font-medium text-white mb-1 line-clamp-2">{post.title}</h3>
        )}

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {post.tags.slice(0, 4).map(tag => (
              <span key={tag} className="text-[10px] text-zenna-cyan/80 bg-zenna-cyan/10 px-1.5 py-0.5 rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 pt-1.5 border-t border-slate-700/50">
          {/* Like */}
          <button
            onClick={() => onLike && onLike(post.id)}
            className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium transition-all min-h-[40px] ${
              post.is_liked
                ? 'text-red-400 bg-red-400/10'
                : 'text-slate-400 hover:text-red-400 hover:bg-red-400/10 active:bg-red-400/20'
            }`}
            aria-label="Like"
          >
            {post.is_liked ? '❤️' : '🤍'} {post.like_count || 0}
          </button>

          {/* Comment */}
          <button
            onClick={handleToggleComments}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium text-slate-400 hover:text-blue-400 hover:bg-blue-400/10 active:bg-blue-400/20 transition-all min-h-[40px]"
            aria-label="Comments"
          >
            💬 {post.comment_count || 0}
          </button>

          {/* Favorite */}
          <button
            onClick={() => onToggleFavorite && onToggleFavorite(post)}
            className={`flex items-center px-2.5 py-2 rounded-lg text-sm transition-all min-h-[40px] min-w-[40px] justify-center ${
              post.is_favorited
                ? 'text-amber-400 bg-amber-400/10'
                : 'text-slate-400 hover:text-amber-400 hover:bg-amber-400/10 active:bg-amber-400/20'
            }`}
            title={post.is_favorited ? 'Unfavorite' : 'Add to favorites'}
            aria-label={post.is_favorited ? 'Unfavorite' : 'Add to favorites'}
          >
            {post.is_favorited ? '⭐' : '☆'}
          </button>

          {/* Remix */}
          <button
            onClick={() => onRemix && onRemix(post)}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium text-slate-400 hover:text-zenna-cyan hover:bg-zenna-cyan/10 active:bg-zenna-cyan/20 transition-all min-h-[40px]"
            title="Remix this design"
            aria-label="Remix"
          >
            🔄 <span className="hidden sm:inline">Remix</span>
          </button>

          {/* Share */}
          <button
            onClick={() => setShowShare(!showShare)}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium text-slate-400 hover:text-zenna-violet hover:bg-zenna-violet/10 active:bg-zenna-violet/20 transition-all ml-auto min-h-[40px]"
            aria-label="Share"
          >
            ↗️ <span className="hidden sm:inline">Share</span>
          </button>
        </div>

        {/* Share buttons (collapsible) */}
        {showShare && (
          <div className="mt-2 pt-2 border-t border-slate-700/30">
            <ShareButtons
              imageUrl={post.thumbnail_url || post.image_urls?.[0]}
              title={post.title}
              pageUrl={`${window.location.origin}/gallery/${post.id}`}
              contentType="gallery_post"
              contentId={post.id}
              userEmail={userEmail}
              compact
              showWatermarkToggle={false}
            />
          </div>
        )}

        {/* Comments section (collapsible) */}
        {showComments && (
          <div className="mt-2 pt-2 border-t border-slate-700/30 space-y-2">
            {comments.map(comment => (
              <div key={comment.id} className="flex gap-2">
                <div className="w-5 h-5 rounded-full bg-slate-700 flex-shrink-0 flex items-center justify-center text-[9px] text-slate-400">
                  {(comment.author?.display_name || '?')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] font-medium text-slate-300">{comment.author?.display_name || 'Anonymous'}</span>
                  <p className="text-[11px] text-slate-400 break-words">{comment.content}</p>
                </div>
              </div>
            ))}

            {/* New comment input */}
            {userEmail && (
              <form onSubmit={handleSubmitComment} className="flex gap-1.5">
                <input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  maxLength={2000}
                  className="flex-1 bg-slate-700/50 border border-slate-600/50 rounded-lg px-2.5 py-1.5 text-[11px] text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="px-2.5 py-1.5 bg-zenna-violet/80 hover:bg-zenna-violet text-white text-[11px] rounded-lg disabled:opacity-30"
                >
                  {submitting ? '...' : '↑'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// POST DETAIL MODAL
// ============================================================================

function PostDetailModal({ post, userEmail, onClose, onLike }) {
  if (!post) return null

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-slate-700 flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>
        {/* Image */}
        <div className="flex-1 bg-black flex items-center justify-center min-h-[300px]">
          <img
            src={post.image_urls?.[0] || post.thumbnail_url}
            alt={post.title}
            className="max-w-full max-h-[80vh] object-contain"
          />
        </div>

        {/* Sidebar */}
        <div className="w-full md:w-80 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-slate-700 flex items-center gap-3">
            {post.author?.avatar_url ? (
              <img src={post.author.avatar_url} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center text-sm text-white font-bold">
                {(post.author?.display_name || '?')[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{post.author?.display_name}</div>
              {post.craft_type && <div className="text-xs text-slate-500">{post.craft_type}</div>}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-lg">✕</button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {post.title && <h2 className="text-base font-semibold text-white mb-2">{post.title}</h2>}
            {post.description && <p className="text-sm text-slate-400 mb-3">{post.description}</p>}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {post.tags.map(tag => (
                  <span key={tag} className="text-[10px] text-zenna-cyan/80 bg-zenna-cyan/10 px-2 py-0.5 rounded-full">#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-4 border-t border-slate-700 space-y-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => onLike && onLike(post.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  post.is_liked ? 'text-red-400 bg-red-400/10' : 'text-slate-400 hover:text-red-400 hover:bg-red-400/10'
                }`}
              >
                {post.is_liked ? '❤️' : '🤍'} {post.like_count || 0}
              </button>
              <span className="text-sm text-slate-500">💬 {post.comment_count || 0}</span>
              <span className="text-sm text-slate-500">👁 {post.view_count || 0}</span>
            </div>

            <ShareButtons
              imageUrl={post.image_urls?.[0]}
              title={post.title}
              pageUrl={`${window.location.origin}/gallery/${post.id}`}
              contentType="gallery_post"
              contentId={post.id}
              userEmail={userEmail}
              compact
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// POST TO GALLERY MODAL
// ============================================================================

function CreatePostModal({ userEmail, onClose, onCreated }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [craftType, setCraftType] = useState('')
  const [tags, setTags] = useState('')
  const [images, setImages] = useState([])
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) return // 10MB limit
      const reader = new FileReader()
      reader.onload = (ev) => {
        setImages(prev => [...prev, { dataUrl: ev.target.result, file }])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx))
  }

  const handleSubmit = async () => {
    if (images.length === 0) return
    setSubmitting(true)
    try {
      // For now, use data URLs as image_urls
      // In production, you'd upload to Supabase Storage first
      const imageUrls = images.map(img => img.dataUrl)

      const res = await fetch(`${API_URL}/community/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({
          title,
          description,
          craft_type: craftType,
          tags: tags.split(',').map(t => t.trim()).filter(Boolean),
          image_urls: imageUrls,
          thumbnail_url: imageUrls[0]
        })
      })

      const data = await res.json()
      if (data.success) {
        if (onCreated) onCreated(data.post)
        onClose()
      }
    } catch (err) {
      console.error('Create post error:', err)
    }
    setSubmitting(false)
  }

  const CRAFT_TYPES = [
    'cross-stitch', 'embroidery', 'sewing', 'quilting', 'crochet', 'knitting',
    'vinyl-decal', 'sublimation', 'laser-cutting', '3d-printing', 'resin-art',
    'paper-craft', 'jewelry', 'woodworking', 'painting', 'other'
  ]

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-700" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Share to Gallery</h2>
          <p className="text-xs text-slate-400 mt-1">Show off your craft to the Zenna community!</p>
        </div>

        <div className="p-5 space-y-4">
          {/* Image upload */}
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Images *</label>
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-600">
                  <img src={img.dataUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 rounded-full text-white text-[10px] flex items-center justify-center"
                  >✕</button>
                </div>
              ))}
              {images.length < 5 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 hover:border-zenna-violet/50 flex flex-col items-center justify-center text-slate-500 hover:text-zenna-violet transition-colors"
                >
                  <span className="text-lg">+</span>
                  <span className="text-[9px]">Add</span>
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
          </div>

          {/* Title */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What did you make?"
              maxLength={200}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us about your project..."
              maxLength={2000}
              rows={3}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50 resize-none"
            />
          </div>

          {/* Craft type */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Craft Type</label>
            <select
              value={craftType}
              onChange={(e) => setCraftType(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-zenna-violet/50"
            >
              <option value="">Select...</option>
              {CRAFT_TYPES.map(ct => (
                <option key={ct} value={ct}>{ct.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tags (comma separated)</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="handmade, gift, holiday"
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50"
            />
          </div>
        </div>

        <div className="p-5 border-t border-slate-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-400 bg-slate-700/50 hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={images.length === 0 || submitting}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-zenna-violet to-zenna-cyan hover:opacity-90 disabled:opacity-30"
          >
            {submitting ? 'Posting...' : '✨ Share'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN GALLERY COMPONENT
// ============================================================================

export default function CommunityGallery({ userEmail = '' }) {
  const navigate = useNavigate()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  // Filters
  const [sortBy, setSortBy] = useState('newest')
  const [craftFilter, setCraftFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')

  // Active challenge
  const [activeChallenge, setActiveChallenge] = useState(null)
  const [challengeFilter, setChallengeFilter] = useState(false)

  // Favorites-only view
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  useEffect(() => {
    fetch(`${API_URL}/community/challenges/active`)
      .then(r => r.json())
      .then(data => setActiveChallenge(data.challenge))
      .catch(() => {})
  }, [])

  // Modals
  const [selectedPost, setSelectedPost] = useState(null)
  const [showCreatePost, setShowCreatePost] = useState(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(searchQuery), 400)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const loadPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true)

      // Favorites-only view: fetch from /favorites instead of /gallery
      if (favoritesOnly) {
        if (!userEmail) {
          setPosts([])
          setHasMore(false)
          setLoading(false)
          return
        }
        const res = await fetch(`${API_URL}/community/favorites`, {
          headers: { 'x-user-email': userEmail }
        })
        const data = await res.json()
        setPosts(data.favorites || [])
        setHasMore(false)
        setLoading(false)
        return
      }

      const params = new URLSearchParams({
        page: pageNum,
        limit: 24,
        sort: sortBy
      })
      if (craftFilter) params.append('craft_type', craftFilter)
      if (searchDebounce.trim()) params.append('search', searchDebounce.trim())
      if (challengeFilter && activeChallenge) params.append('challenge_id', activeChallenge.id)

      const res = await fetch(`${API_URL}/community/gallery?${params}`, {
        headers: userEmail ? { 'x-user-email': userEmail } : {}
      })
      const data = await res.json()

      if (append) {
        setPosts(prev => [...prev, ...(data.posts || [])])
      } else {
        setPosts(data.posts || [])
      }

      setHasMore((data.posts || []).length >= 24)
    } catch (err) {
      console.error('Load gallery error:', err)
    } finally {
      setLoading(false)
    }
  }, [sortBy, craftFilter, searchDebounce, challengeFilter, activeChallenge, favoritesOnly, userEmail])

  useEffect(() => {
    setPage(1)
    loadPosts(1)
  }, [loadPosts])

  const loadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    loadPosts(nextPage, true)
  }

  const handleToggleFavorite = async (post) => {
    if (!userEmail) return
    try {
      const res = await fetch(`${API_URL}/community/favorites/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({ post_id: post.id })
      })
      const data = await res.json()
      if (data.success) {
        setPosts(prev => prev.map(p =>
          p.id === post.id ? { ...p, is_favorited: data.favorited } : p
        ))
      }
    } catch (e) { console.error(e) }
  }

  // After loading posts, enrich with favorite status
  useEffect(() => {
    if (!userEmail || posts.length === 0) return
    const postIds = posts.map(p => p.id)
    fetch(`${API_URL}/community/favorites/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
      body: JSON.stringify({ post_ids: postIds })
    })
      .then(r => r.json())
      .then(data => {
        if (data.favorited) {
          setPosts(prev => prev.map(p => ({ ...p, is_favorited: !!data.favorited[p.id] })))
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, userEmail])

  const handleLike = async (postId) => {
    if (!userEmail) return
    try {
      const res = await fetch(`${API_URL}/community/gallery/${postId}/like`, {
        method: 'POST',
        headers: { 'x-user-email': userEmail }
      })
      const data = await res.json()
      if (data.success) {
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, is_liked: data.liked, like_count: (p.like_count || 0) + (data.liked ? 1 : -1) }
            : p
        ))
        if (selectedPost?.id === postId) {
          setSelectedPost(prev => ({
            ...prev,
            is_liked: data.liked,
            like_count: (prev.like_count || 0) + (data.liked ? 1 : -1)
          }))
        }
      }
    } catch (e) { console.error(e) }
  }

  const CRAFT_FILTERS = [
    { value: '', label: 'All Crafts' },
    { value: 'cross-stitch', label: 'Cross Stitch' },
    { value: 'sewing', label: 'Sewing' },
    { value: 'quilting', label: 'Quilting' },
    { value: 'crochet', label: 'Crochet' },
    { value: 'knitting', label: 'Knitting' },
    { value: 'macrame', label: 'Macrame' },
    { value: 'perler-beads', label: 'Perler Beads' },
    { value: 'vinyl-decal', label: 'Vinyl' },
    { value: 'sublimation', label: 'Sublimation' },
    { value: 'screen-printing', label: 'Screen Print' },
    { value: 'stained-glass', label: 'Stained Glass' },
    { value: 'punch-needle', label: 'Punch Needle' },
    { value: 'diamond-painting', label: 'Diamond Paint' },
    { value: 'wood-burning', label: 'Wood Burn' },
    { value: 'resin-art', label: 'Resin' },
    { value: 'coloring-page', label: 'Coloring' },
    { value: 'embroidery', label: 'Embroidery' },
    { value: 'applique', label: 'Applique' },
    { value: 'latch-hook', label: 'Latch Hook' },
    { value: 'tattoo-flash', label: 'Tattoo Flash' },
    { value: 't-shirt-design', label: 'T-Shirt' },
    { value: 'wall-art', label: 'Wall Art' },
  ]

  return (
    <div className="min-h-screen bg-slate-900">
      <ZennaPageHeader title="Gallery" />
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-white">Community Gallery</h1>
              <p className="text-xs text-slate-500">Inspiration from the Zenna community</p>
            </div>

            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative hidden sm:block">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search designs..."
                  className="w-48 pl-8 pr-3 py-1.5 text-xs rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs">✕</button>
                )}
              </div>

              {userEmail && (
                <button
                  onClick={() => setShowCreatePost(true)}
                  className="px-4 py-2 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
                >
                  ✨ Share Your Work
                </button>
              )}
            </div>
          </div>

          {/* Active Challenge Banner */}
          {activeChallenge && (
            <div
              onClick={() => setChallengeFilter(!challengeFilter)}
              className={`mb-3 p-3 rounded-xl cursor-pointer transition-all border ${
                challengeFilter
                  ? 'bg-gradient-to-r from-amber-500/20 to-pink-500/20 border-amber-400/50'
                  : 'bg-slate-800/60 border-slate-700 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="text-3xl">{activeChallenge.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">🏆 Current Challenge</span>
                    {challengeFilter && <span className="text-[9px] bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded-full">Filtering entries</span>}
                  </div>
                  <div className="text-sm font-semibold text-white">{activeChallenge.title}</div>
                  <div className="text-xs text-slate-400 line-clamp-1">{activeChallenge.description}</div>
                </div>
                <div className="text-[10px] text-slate-500 hidden sm:block text-right">
                  Ends {new Date(activeChallenge.ends).toLocaleDateString()}
                  <div className="text-amber-400/80">{activeChallenge.prize}</div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile search */}
          <div className="sm:hidden mb-3">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search designs..."
                className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            {/* Sort */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-0.5 flex-shrink-0">
              {[
                { value: 'newest', label: '🆕 New' },
                { value: 'popular', label: '🔥 Popular' },
                { value: 'featured', label: '⭐ Featured' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                    sortBy === opt.value
                      ? 'bg-zenna-violet/20 text-zenna-violet'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Favorites toggle */}
            {userEmail && (
              <button
                onClick={() => setFavoritesOnly(!favoritesOnly)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  favoritesOnly
                    ? 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                    : 'text-slate-500 bg-slate-800 hover:text-amber-300 border border-transparent'
                }`}
                title="Show only your favorites"
              >
                ⭐ My Favorites
              </button>
            )}

            {/* Craft filter pills */}
            {CRAFT_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setCraftFilter(f.value)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  craftFilter === f.value
                    ? 'bg-zenna-cyan/20 text-zenna-cyan border border-zenna-cyan/30'
                    : 'text-slate-500 bg-slate-800 hover:text-slate-300 border border-transparent'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Masonry Grid */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && posts.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-zenna-violet border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading gallery...</p>
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-4xl mb-3">🎨</p>
            <h3 className="text-lg font-medium text-white mb-1">Gallery is empty!</h3>
            <p className="text-sm text-slate-500 mb-4">Be the first to share your craft.</p>
            {userEmail && (
              <button
                onClick={() => setShowCreatePost(true)}
                className="px-5 py-2.5 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium rounded-xl"
              >
                ✨ Share Your Work
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="columns-1 min-[420px]:columns-2 sm:columns-2 md:columns-3 lg:columns-4 gap-3 sm:gap-4 space-y-3 sm:space-y-4">
              {posts.map(post => (
                <div key={post.id} className="break-inside-avoid">
                  <PostCard
                    post={post}
                    userEmail={userEmail}
                    onLike={handleLike}
                    onViewPost={setSelectedPost}
                    onToggleFavorite={handleToggleFavorite}
                    onRemix={(p) => {
                      const img = p.image_urls?.[0] || p.thumbnail_url
                      navigate(`/workspace?remix=true&remixImage=${encodeURIComponent(img)}&remixCraft=${encodeURIComponent(p.craft_type || '')}&remixTitle=${encodeURIComponent('Remix of: ' + (p.title || 'Gallery design'))}`)
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-sm text-slate-300 rounded-xl border border-slate-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          userEmail={userEmail}
          onClose={() => setSelectedPost(null)}
          onLike={handleLike}
        />
      )}

      {showCreatePost && (
        <CreatePostModal
          userEmail={userEmail}
          onClose={() => setShowCreatePost(false)}
          onCreated={(newPost) => setPosts(prev => [newPost, ...prev])}
        />
      )}
    </div>
  )
}